import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BarChart3, CheckCheck, Download, Filter, Inbox, KanbanSquare, List, Loader2, RefreshCw, Search, Settings2, Star, UserPlus, X,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { guardarBlob, nombreDeDisposicion } from '../respuestas/datos';
import { mensajeDe } from '../responder/utilidades';
import DetalleCaso from './DetalleCaso';
import Estadisticas from './Estadisticas';
import SelectorPersona, { Avatar } from './SelectorPersona';
import {
  ESTADOS, NOMBRE_ESTADO, NOMBRE_PRIORIDAD, ORDENES, PRIORIDADES, VISTAS, haceCuanto, textoPlazo,
} from './constantes';
import './buzon.css';

/*
 * La bandeja del buzón de casos (lo que en Jotform es el Inbox + las asignaciones).
 *
 *   <BandejaCasos formId? onConfigurar? />
 *
 * Con `formId`: los casos de ESE buzón (quien tiene el formulario ve todos; quien solo tiene casos
 * asignados, los suyos). Sin `formId`: «mis casos», lo asignado a la persona en todos los buzones.
 * El caso abierto vive en la URL (`?caso=<id>`): los avisos (campana, push, correo) llevan ahí.
 * Filtrar, contar y ordenar lo hace el servidor (buzon.filtrar / contadores / ordenar).
 */

const POR_PAGINA = 50;
const FILTROS_VACIOS = { estado: '', categoria: '', prioridad: '', asignado: '', etiqueta: '' };

export default function BandejaCasos({ formId = null, onConfigurar = null }) {
  const movil = useIsMobile(900);
  const [params, setParams] = useSearchParams();
  const abierto = params.get('caso');
  const [vista, setVista] = useState(formId ? 'abiertos' : 'mios');
  const [modo, setModo] = useState('lista'); // lista | tablero | estadisticas
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [verFiltros, setVerFiltros] = useState(false);
  const [q, setQ] = useState('');
  const [qAplicada, setQAplicada] = useState('');
  const [orden, setOrden] = useState('recientes');
  const [pagina, setPagina] = useState(0);
  const [datos, setDatos] = useState({ cargando: true, items: [], total: 0, contadores: {}, etiquetas: [], buzon: null, error: null });
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [ocupado, setOcupado] = useState(false);
  const version = useRef(0);

  // Búsqueda: espera a que se deje de escribir
  useEffect(() => {
    const t = setTimeout(() => setQAplicada(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPagina(0); setSeleccion(new Set()); }, [vista, filtros, qAplicada, orden, formId]);

  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    const mia = ++version.current;
    if (!silencioso) setDatos((d) => ({ ...d, cargando: true, error: null }));
    try {
      const { data } = await api.get('/buzon/casos', {
        params: {
          form_id: formId || undefined, vista: modo === 'tablero' ? 'todos' : vista, orden, q: qAplicada || undefined,
          desde: modo === 'tablero' ? 0 : pagina * POR_PAGINA, limite: modo === 'tablero' ? 200 : POR_PAGINA,
          ...Object.fromEntries(Object.keys(FILTROS_VACIOS).filter((k) => filtros[k]).map((k) => [k, filtros[k]])),
        },
      });
      if (mia !== version.current) return; // llegó tarde: ya se pidió otra cosa
      setDatos({ cargando: false, error: null, ...data, items: data.items || [] });
    } catch (err) {
      if (mia !== version.current) return;
      setDatos((d) => ({ ...d, cargando: false, error: mensajeDe(err, 'No se pudieron cargar los casos') }));
    }
  }, [formId, vista, orden, qAplicada, pagina, filtros, modo]);

  useEffect(() => { cargar(); }, [cargar]);

  // Casos nuevos de otras personas: se consulta sola cada 20 s mientras la pestaña está a la vista
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') cargar({ silencioso: true }); }, 20000);
    return () => clearInterval(t);
  }, [cargar]);

  const abrir = (rid) => {
    const p = new URLSearchParams(params);
    if (rid) p.set('caso', rid);
    else p.delete('caso');
    setParams(p, { replace: !rid });
  };

  // El detalle avisa de lo que cambió para que la fila no se quede atrás
  const alCambiarCaso = useCallback((fila) => {
    setDatos((d) => {
      if (!fila) return { ...d, items: d.items.filter((it) => it.id !== abierto) };
      return { ...d, items: d.items.map((it) => (it.id === fila.id ? { ...it, ...fila } : it)) };
    });
  }, [abierto]);

  const buzon = datos.buzon;
  const permisos = buzon?.permisos || null;
  const gestor = !!permisos?.gestionar;
  const categorias = buzon?.categorias || [];
  const vistas = VISTAS.filter((v) => formId || !v.soloGestor);
  const nFiltros = Object.keys(FILTROS_VACIOS).filter((k) => filtros[k]).length;
  const hayFiltros = nFiltros > 0;

  /* ---------- Selección y acciones en lote ---------- */
  const alternar = (id) => setSeleccion((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const todosMarcados = datos.items.length > 0 && datos.items.every((it) => seleccion.has(it.id));
  const lote = async (cambios, texto) => {
    setOcupado(true);
    try {
      const { data } = await api.post('/buzon/casos/lote', { ids: [...seleccion], cambios });
      toast.success(`${texto}: ${data.actualizados} ${data.actualizados === 1 ? 'caso' : 'casos'}${data.omitidos ? ` · ${data.omitidos} sin permiso` : ''}`);
      setSeleccion(new Set());
      cargar({ silencioso: true });
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo aplicar a los casos elegidos'));
    } finally {
      setOcupado(false);
    }
  };
  const marcarLeidos = async () => {
    setOcupado(true);
    try {
      await Promise.all([...seleccion].map((id) => api.put(`/buzon/casos/${id}/marcas`, { leido: true })));
      setSeleccion(new Set());
      cargar({ silencioso: true });
    } finally {
      setOcupado(false);
    }
  };
  const destacar = async (fila) => {
    try {
      await api.put(`/buzon/casos/${fila.id}/marcas`, { destacado: !fila.destacado });
      setDatos((d) => ({ ...d, items: d.items.map((it) => (it.id === fila.id ? { ...it, destacado: !fila.destacado } : it)) }));
    } catch (err) {
      toast.error('No se pudo destacar');
    }
  };

  const exportar = async () => {
    setOcupado(true);
    try {
      const res = await api.get('/buzon/exportar', { params: { form_id: formId }, responseType: 'blob' });
      guardarBlob(res.data, nombreDeDisposicion(res.headers?.['content-disposition']) || 'casos.csv');
    } catch (err) {
      toast.error('No se pudieron descargar los casos');
    } finally {
      setOcupado(false);
    }
  };

  const moverEnTablero = async (rid, estado) => {
    const fila = datos.items.find((it) => it.id === rid);
    if (!fila || fila.estado === estado) return;
    setDatos((d) => ({ ...d, items: d.items.map((it) => (it.id === rid ? { ...it, estado } : it)) }));
    try {
      await api.patch(`/buzon/casos/${rid}`, { estado });
      toast.success(`${fila.folio}: ${NOMBRE_ESTADO[estado]}`);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo mover el caso'));
      cargar({ silencioso: true });
    }
  };

  /* ---------- Piezas ---------- */
  const barraLateral = (
    <nav className={`bz-vistas ${movil ? 'is-movil' : ''}`} aria-label="Vistas de la bandeja">
      {vistas.map((v) => (
        <button
          key={v.id}
          type="button"
          className={`bz-vista ${vista === v.id && modo !== 'estadisticas' ? 'is-activa' : ''}`}
          aria-current={vista === v.id ? 'true' : undefined}
          onClick={() => { setVista(v.id); if (modo === 'estadisticas') setModo('lista'); }}
        >
          <span className="truncate">{v.nombre}</span>
          {(datos.contadores?.[v.id] ?? 0) > 0 && (
            <span className={`bz-contador ${v.id === 'vencidos' ? 'is-peligro' : ''}`}>{datos.contadores[v.id]}</span>
          )}
        </button>
      ))}
    </nav>
  );

  const detalle = abierto ? (
    <DetalleCaso key={abierto} rid={abierto} onCerrar={() => abrir(null)} onCambio={alCambiarCaso} volver={movil} />
  ) : null;

  if (movil && abierto) {
    return <div className="bz-raiz is-movil-detalle">{detalle}</div>;
  }

  return (
    <div className={`bz-raiz ${movil ? 'is-movil' : ''}`}>
      <div className="bz-barra">
        <div className="bz-buscar">
          <Search className="h-4 w-4" aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por folio, texto, correo o etiqueta" aria-label="Buscar casos" />
          {q && <button type="button" onClick={() => setQ('')} aria-label="Borrar búsqueda"><X className="h-4 w-4" /></button>}
        </div>
        <button type="button" className={`bz-boton-borde ${hayFiltros || verFiltros ? 'is-activo' : ''}`} onClick={() => setVerFiltros((v) => !v)} aria-expanded={verFiltros}>
          <Filter className="h-4 w-4" /> Filtros{hayFiltros ? ` (${nFiltros})` : ''}
        </button>
        <select className="bz-select is-compacto" value={orden} onChange={(e) => setOrden(e.target.value)} aria-label="Ordenar">
          {ORDENES.map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}
        </select>
        <div className="bz-modos" role="tablist" aria-label="Vista">
          <button type="button" role="tab" aria-selected={modo === 'lista'} className={modo === 'lista' ? 'is-activo' : ''} onClick={() => setModo('lista')} title="Lista"><List className="h-4 w-4" /><span>Lista</span></button>
          <button type="button" role="tab" aria-selected={modo === 'tablero'} className={modo === 'tablero' ? 'is-activo' : ''} onClick={() => setModo('tablero')} title="Tablero"><KanbanSquare className="h-4 w-4" /><span>Tablero</span></button>
          <button type="button" role="tab" aria-selected={modo === 'estadisticas'} className={modo === 'estadisticas' ? 'is-activo' : ''} onClick={() => setModo('estadisticas')} title="Estadísticas"><BarChart3 className="h-4 w-4" /><span>Estadísticas</span></button>
        </div>
        <button type="button" className="od-tool-btn" onClick={() => cargar()} aria-label="Actualizar" title="Actualizar">
          <RefreshCw className={`h-[18px] w-[18px] ${datos.cargando ? 'od-spin' : ''}`} />
        </button>
        {formId && buzon?.rol && (
          <button type="button" className="od-tool-btn" onClick={exportar} disabled={ocupado} aria-label="Descargar casos (.csv)" title="Descargar casos (.csv)">
            <Download className="h-[18px] w-[18px]" />
          </button>
        )}
        {onConfigurar && gestor && (
          <button type="button" className="bz-boton-borde" onClick={onConfigurar}><Settings2 className="h-4 w-4" /> Configurar buzón</button>
        )}
      </div>

      {verFiltros && (
        <div className="bz-filtros">
          <select className="bz-select is-compacto" value={filtros.estado} onChange={(e) => setFiltros((f) => ({ ...f, estado: e.target.value }))} aria-label="Filtrar por estado">
            <option value="">Cualquier estado</option>
            {ESTADOS.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
          <select className="bz-select is-compacto" value={filtros.prioridad} onChange={(e) => setFiltros((f) => ({ ...f, prioridad: e.target.value }))} aria-label="Filtrar por prioridad">
            <option value="">Cualquier prioridad</option>
            {PRIORIDADES.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
          {categorias.length > 0 && (
            <select className="bz-select is-compacto" value={filtros.categoria} onChange={(e) => setFiltros((f) => ({ ...f, categoria: e.target.value }))} aria-label="Filtrar por categoría">
              <option value="">Cualquier categoría</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          )}
          {formId && gestor && (
            <SelectorPersona formId={formId} valor={filtros.asignadoPersona || null} permitirNadie textoNadie="Sin asignar"
              onElegir={(p) => setFiltros((f) => ({ ...f, asignado: p ? p.id : 'nadie', asignadoPersona: p }))}
            >
              <button type="button" className="bz-boton-borde">
                <UserPlus className="h-4 w-4" />
                {filtros.asignado === 'nadie' ? 'Sin asignar' : filtros.asignadoPersona?.nombre || 'Cualquier persona'}
              </button>
            </SelectorPersona>
          )}
          {datos.etiquetas?.length > 0 && (
            <select className="bz-select is-compacto" value={filtros.etiqueta} onChange={(e) => setFiltros((f) => ({ ...f, etiqueta: e.target.value }))} aria-label="Filtrar por etiqueta">
              <option value="">Cualquier etiqueta</option>
              {datos.etiquetas.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          )}
          {hayFiltros && <button type="button" className="bz-boton-texto" onClick={() => setFiltros(FILTROS_VACIOS)}>Quitar filtros</button>}
        </div>
      )}

      <div className={`bz-cuerpo is-${modo} ${abierto && modo === 'lista' ? 'con-detalle' : ''}`}>
        {modo !== 'tablero' && barraLateral}

        {modo === 'estadisticas' && <Estadisticas formId={formId} />}

        {modo === 'lista' && (
          <section className="bz-lista" aria-label="Casos">
            {seleccion.size > 0 ? (
              <div className="bz-lote" role="toolbar" aria-label="Acciones sobre los casos elegidos">
                <input type="checkbox" checked={todosMarcados} onChange={() => setSeleccion(todosMarcados ? new Set() : new Set(datos.items.map((it) => it.id)))} aria-label="Elegir todos" />
                <span className="bz-lote-n">{seleccion.size} elegidos</span>
                <SelectorPersona formId={formId} valor={null} permitirNadie={gestor} onElegir={(p) => lote({ asignado_id: p?.id || null }, p ? `Asignados a ${p.nombre}` : 'Sin asignar')}>
                  <button type="button" className="bz-boton-borde" disabled={ocupado}><UserPlus className="h-4 w-4" /> Asignar</button>
                </SelectorPersona>
                <select className="bz-select is-compacto" value="" disabled={ocupado} onChange={(e) => e.target.value && lote({ estado: e.target.value }, NOMBRE_ESTADO[e.target.value])} aria-label="Cambiar estado">
                  <option value="">Estado…</option>
                  {ESTADOS.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
                <select className="bz-select is-compacto" value="" disabled={ocupado} onChange={(e) => e.target.value && lote({ prioridad: e.target.value }, `Prioridad ${NOMBRE_PRIORIDAD[e.target.value].toLowerCase()}`)} aria-label="Cambiar prioridad">
                  <option value="">Prioridad…</option>
                  {PRIORIDADES.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
                <button type="button" className="bz-boton-texto" onClick={marcarLeidos} disabled={ocupado}><CheckCheck className="h-4 w-4" /> Marcar leídos</button>
                <button type="button" className="od-tool-btn ml-auto" onClick={() => setSeleccion(new Set())} aria-label="Quitar selección"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="bz-lista-cabecera">
                {datos.items.length > 0 && (
                  <input type="checkbox" checked={false} onChange={() => setSeleccion(new Set(datos.items.map((it) => it.id)))} aria-label="Elegir todos los de esta página" />
                )}
                <span>{datos.total} {datos.total === 1 ? 'caso' : 'casos'}</span>
                {datos.total > POR_PAGINA && (
                  <span className="bz-paginacion">
                    {pagina * POR_PAGINA + 1}–{Math.min(datos.total, (pagina + 1) * POR_PAGINA)} de {datos.total}
                    <button type="button" className="od-tool-btn" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)} aria-label="Página anterior">‹</button>
                    <button type="button" className="od-tool-btn" disabled={(pagina + 1) * POR_PAGINA >= datos.total} onClick={() => setPagina((p) => p + 1)} aria-label="Página siguiente">›</button>
                  </span>
                )}
              </div>
            )}
            <div className="bz-filas od-scroll">
              {datos.cargando && datos.items.length === 0 && <div className="bz-centro"><Loader2 className="h-6 w-6 od-spin" aria-label="Cargando" /></div>}
              {datos.error && <div className="bz-centro"><p>{datos.error}</p></div>}
              {!datos.cargando && !datos.error && datos.items.length === 0 && (
                <div className="bz-centro bz-vacia">
                  <Inbox className="h-10 w-10" aria-hidden="true" />
                  <p>{qAplicada || hayFiltros ? 'Ningún caso coincide.' : vista === 'mios' ? 'No tienes casos asignados.' : 'No hay casos en esta vista.'}</p>
                </div>
              )}
              {datos.items.map((it) => (
                <FilaCaso
                  key={it.id}
                  fila={it}
                  activa={it.id === abierto}
                  elegida={seleccion.has(it.id)}
                  conFormulario={!formId}
                  onAbrir={() => abrir(it.id)}
                  onElegir={() => alternar(it.id)}
                  onDestacar={() => destacar(it)}
                />
              ))}
            </div>
          </section>
        )}

        {modo === 'lista' && !movil && (
          detalle || (
            <div className="bz-detalle bz-centro bz-sin-detalle">
              <Inbox className="h-10 w-10" aria-hidden="true" />
              <p>Elige un caso para verlo, asignarlo y responder.</p>
            </div>
          )
        )}

        {modo === 'tablero' && (
          <Tablero items={datos.items} cargando={datos.cargando} onAbrir={abrir} onMover={moverEnTablero} conFormulario={!formId} />
        )}
      </div>

      {modo === 'tablero' && abierto && (
        <div className="bz-hoja" role="dialog" aria-label="Caso">
          <div className="bz-hoja-fondo" onClick={() => abrir(null)} aria-hidden="true" />
          <div className="bz-hoja-panel">{detalle}</div>
        </div>
      )}
    </div>
  );
}

function FilaCaso({ fila, activa, elegida, conFormulario, onAbrir, onElegir, onDestacar }) {
  const abierto = ESTADOS.find((e) => e.id === fila.estado)?.abierto;
  return (
    <div
      className={`bz-fila ${activa ? 'is-activa' : ''} ${fila.leido ? '' : 'is-no-leida'} ${elegida ? 'is-elegida' : ''}`}
      role="button"
      tabIndex={0}
      onClick={onAbrir}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(); } }}
      aria-label={`${fila.folio}${fila.leido ? '' : ', no leído'}`}
    >
      <input type="checkbox" checked={elegida} onClick={(e) => e.stopPropagation()} onChange={onElegir} aria-label={`Elegir ${fila.folio}`} />
      <button
        type="button"
        className={`bz-estrella ${fila.destacado ? 'is-on' : ''}`}
        onClick={(e) => { e.stopPropagation(); onDestacar(); }}
        aria-label={fila.destacado ? 'Quitar de destacados' : 'Destacar'}
        aria-pressed={!!fila.destacado}
      >
        <Star className="h-4 w-4" />
      </button>
      <div className="bz-fila-cuerpo">
        <div className="bz-fila-linea">
          <span className="bz-fila-folio">{fila.folio}</span>
          {fila.categoria_info && (
            <span className="bz-cat"><span className="bz-punto" style={{ background: fila.categoria_info.color }} aria-hidden="true" />{fila.categoria_info.nombre}</span>
          )}
          <span className={`bz-estado is-${fila.estado}`}>{NOMBRE_ESTADO[fila.estado]}</span>
          {fila.prioridad && fila.prioridad !== 'media' && <span className={`bz-prio-chip is-${fila.prioridad}`}>{NOMBRE_PRIORIDAD[fila.prioridad]}</span>}
          {fila.mensajes_ciudadano > 0 && !fila.leido && <span className="bz-chip">Nuevo mensaje</span>}
          <time className="bz-fila-hora" dateTime={fila.creado_en}>{haceCuanto(fila.creado_en)}</time>
        </div>
        <p className="bz-fila-texto">{fila.resumen_ia || fila.vista_previa || <span className="bz-vacio">Sin texto</span>}</p>
        <div className="bz-fila-linea is-pie">
          {conFormulario && <span className="bz-fila-form truncate">{fila.formulario}</span>}
          <span className="bz-fila-asignado">
            {fila.asignado ? <><Avatar persona={fila.asignado} tam={18} /> {fila.asignado.nombre}</> : <span className="bz-vacio">Sin asignar</span>}
          </span>
          {fila.vence_en && (
            <span className={`bz-fila-plazo ${fila.vencido ? 'bz-texto-peligro' : ''}`}>{textoPlazo(fila.vence_en, abierto)}</span>
          )}
          {fila.etiquetas?.slice(0, 2).map((e) => <span key={e} className="bz-chip is-mini">{e}</span>)}
        </div>
      </div>
    </div>
  );
}

function Tablero({ items, cargando, onAbrir, onMover, conFormulario }) {
  const [sobre, setSobre] = useState(null);
  const columnas = useMemo(() => ESTADOS.map((e) => ({ ...e, items: items.filter((it) => it.estado === e.id) })), [items]);
  return (
    <div className="bz-tablero od-scroll" aria-label="Tablero de casos por estado">
      {columnas.map((col) => (
        <section
          key={col.id}
          className={`bz-columna ${sobre === col.id ? 'is-sobre' : ''}`}
          aria-label={col.nombre}
          onDragOver={(e) => { e.preventDefault(); setSobre(col.id); }}
          onDragLeave={() => setSobre((s) => (s === col.id ? null : s))}
          onDrop={(e) => {
            e.preventDefault();
            setSobre(null);
            const rid = e.dataTransfer.getData('text/plain');
            if (rid) onMover(rid, col.id);
          }}
        >
          <h3 className="bz-columna-titulo">
            <span className={`bz-estado is-${col.id}`}>{col.nombre}</span>
            <span className="bz-columna-n">{col.items.length}</span>
          </h3>
          <div className="bz-columna-tarjetas">
            {col.items.map((it) => (
              <button
                key={it.id}
                type="button"
                className={`bz-tarjeta-caso ${it.leido ? '' : 'is-no-leida'}`}
                draggable
                onDragStart={(e) => { e.dataTransfer.setData('text/plain', it.id); e.dataTransfer.effectAllowed = 'move'; }}
                onClick={() => onAbrir(it.id)}
              >
                <span className="bz-fila-linea">
                  <span className="bz-fila-folio">{it.folio}</span>
                  {it.prioridad && it.prioridad !== 'media' && <span className={`bz-prio-chip is-${it.prioridad}`}>{NOMBRE_PRIORIDAD[it.prioridad]}</span>}
                </span>
                {it.categoria_info && (
                  <span className="bz-cat"><span className="bz-punto" style={{ background: it.categoria_info.color }} aria-hidden="true" />{it.categoria_info.nombre}</span>
                )}
                <span className="bz-tarjeta-caso-texto">{it.resumen_ia || it.vista_previa}</span>
                {conFormulario && <span className="bz-fila-form truncate">{it.formulario}</span>}
                <span className="bz-fila-linea is-pie">
                  {it.asignado ? <Avatar persona={it.asignado} tam={20} /> : <span className="bz-vacio">Sin asignar</span>}
                  {it.vence_en && <span className={`bz-fila-plazo ${it.vencido ? 'bz-texto-peligro' : ''}`}>{textoPlazo(it.vence_en, col.abierto)}</span>}
                </span>
              </button>
            ))}
            {!cargando && col.items.length === 0 && <p className="bz-columna-vacia">Arrastra aquí un caso</p>}
          </div>
        </section>
      ))}
    </div>
  );
}
